/**
 * Populations and the sampling distribution of the mean, for the central limit theorem
 * simulator.
 *
 * Each population states its exact mean, standard deviation and skewness (so the
 * simulation can be held against theory: the sample means should centre on μ, spread
 * σ/√n and have skewness γ/√n) and how to draw from it. Draws come from a seeded
 * generator, so a run is reproducible from its seed.
 */

import { createNormalSampler, createRng, type Rng } from "./prng";

export type PopulationId =
  | "normal"
  | "uniform"
  | "skewed"
  | "verySkewed"
  | "bimodal"
  | "binary";

export interface Population {
  id: PopulationId;
  mean: number;
  sd: number;
  skewness: number;
  /** Where the density sketch runs. */
  range: [number, number];
  /** Density at x; null for the binary population, drawn as two bars. */
  pdf: ((x: number) => number) | null;
  /** A draw sampler using the given uniform and normal sources. */
  draw: (rng: Rng, normal: () => number) => number;
}

const SQRT_2PI = Math.sqrt(2 * Math.PI);

function gaussian(x: number, mean: number, sd: number): number {
  const z = (x - mean) / sd;
  return Math.exp(-0.5 * z * z) / (sd * SQRT_2PI);
}

/** Bernoulli success probability of the yes/no population. */
export const BINARY_P = 0.2;

/** The two humps of the bimodal population: N(2, 1) and N(8, 1), equally likely. */
const BIMODAL_LOW = 2;
const BIMODAL_HIGH = 8;

export const POPULATIONS: Record<PopulationId, Population> = {
  normal: {
    id: "normal",
    mean: 0,
    sd: 1,
    skewness: 0,
    range: [-4, 4],
    pdf: (x) => gaussian(x, 0, 1),
    draw: (_rng, normal) => normal(),
  },
  uniform: {
    id: "uniform",
    mean: 0.5,
    sd: Math.sqrt(1 / 12),
    skewness: 0,
    range: [-0.1, 1.1],
    pdf: (x) => (x >= 0 && x <= 1 ? 1 : 0),
    draw: (rng) => rng(),
  },
  // Exponential with rate 1: mean 1, SD 1, skewness 2.
  skewed: {
    id: "skewed",
    mean: 1,
    sd: 1,
    skewness: 2,
    range: [0, 6],
    pdf: (x) => (x >= 0 ? Math.exp(-x) : 0),
    draw: (rng) => -Math.log(1 - rng()),
  },
  // Lognormal(0, 1): mean e^½, variance (e − 1)e, skewness (e + 2)√(e − 1).
  verySkewed: {
    id: "verySkewed",
    mean: Math.exp(0.5),
    sd: Math.sqrt((Math.E - 1) * Math.E),
    skewness: (Math.E + 2) * Math.sqrt(Math.E - 1),
    range: [0, 8],
    pdf: (x) =>
      x > 0 ? Math.exp(-0.5 * Math.log(x) ** 2) / (x * SQRT_2PI) : 0,
    draw: (_rng, normal) => Math.exp(normal()),
  },
  // Equal mixture of N(2, 1) and N(8, 1): mean 5, variance 1 + 3² = 10, symmetric.
  bimodal: {
    id: "bimodal",
    mean: (BIMODAL_LOW + BIMODAL_HIGH) / 2,
    sd: Math.sqrt(10),
    skewness: 0,
    range: [-1, 11],
    pdf: (x) =>
      0.5 * gaussian(x, BIMODAL_LOW, 1) + 0.5 * gaussian(x, BIMODAL_HIGH, 1),
    draw: (rng, normal) =>
      (rng() < 0.5 ? BIMODAL_LOW : BIMODAL_HIGH) + normal(),
  },
  binary: {
    id: "binary",
    mean: BINARY_P,
    sd: Math.sqrt(BINARY_P * (1 - BINARY_P)),
    skewness: (1 - 2 * BINARY_P) / Math.sqrt(BINARY_P * (1 - BINARY_P)),
    range: [0, 1],
    pdf: null,
    draw: (rng) => (rng() < BINARY_P ? 1 : 0),
  },
};

export const POPULATION_IDS = Object.keys(POPULATIONS) as PopulationId[];

/** What the central limit theorem predicts for the mean of n draws. */
export function theoryForMean(population: Population, n: number) {
  return {
    mean: population.mean,
    se: population.sd / Math.sqrt(n),
    skewness: population.skewness / Math.sqrt(n),
  };
}

/** `count` sample means, each of `n` draws from the population, from a seed text. */
export function simulateMeans(
  population: Population,
  n: number,
  count: number,
  seed: string
): Float64Array {
  const rng = createRng(seed);
  const normal = createNormalSampler(rng);
  const means = new Float64Array(count);
  for (let i = 0; i < count; i++) {
    let sum = 0;
    for (let j = 0; j < n; j++) {
      sum += population.draw(rng, normal);
    }
    means[i] = sum / n;
  }
  return means;
}

export interface Moments {
  count: number;
  mean: number;
  sd: number;
  skewness: number;
}

/** Mean, sample SD (n − 1) and moment skewness of the first `count` values. */
export function momentsOf(values: ArrayLike<number>, count: number): Moments {
  const size = Math.min(count, values.length);
  if (size === 0) {
    return { count: 0, mean: 0, sd: 0, skewness: 0 };
  }
  let sum = 0;
  for (let i = 0; i < size; i++) {
    sum += values[i];
  }
  const mean = sum / size;
  let m2 = 0;
  let m3 = 0;
  for (let i = 0; i < size; i++) {
    const d = values[i] - mean;
    m2 += d * d;
    m3 += d * d * d;
  }
  const variance = size > 1 ? m2 / (size - 1) : 0;
  const biased = m2 / size;
  return {
    count: size,
    mean,
    sd: Math.sqrt(variance),
    skewness: biased > 0 ? m3 / size / biased ** 1.5 : 0,
  };
}

export interface Histogram {
  counts: number[];
  /** Values inside the first `count` that fall outside [lo, hi]. */
  outside: number;
  binWidth: number;
}

/** Counts of the first `count` values in `bins` equal bins over [lo, hi). */
export function histogramOf(
  values: ArrayLike<number>,
  count: number,
  lo: number,
  hi: number,
  bins: number
): Histogram {
  const counts = new Array<number>(bins).fill(0);
  const binWidth = (hi - lo) / bins;
  const size = Math.min(count, values.length);
  let outside = 0;
  for (let i = 0; i < size; i++) {
    const value = values[i];
    if (value < lo || value > hi) {
      outside += 1;
    } else {
      counts[Math.min(bins - 1, Math.floor((value - lo) / binWidth))] += 1;
    }
  }
  return { counts, outside, binWidth };
}

/** Share of the first `count` values within ±z standard errors of the centre. */
export function shareWithin(
  values: ArrayLike<number>,
  count: number,
  centre: number,
  se: number,
  z: number
): number {
  const size = Math.min(count, values.length);
  if (size === 0) {
    return 0;
  }
  let inside = 0;
  for (let i = 0; i < size; i++) {
    if (Math.abs(values[i] - centre) <= z * se) {
      inside += 1;
    }
  }
  return inside / size;
}
